/**
 * Widget Options Menu Component
 * Dropdown menu for widget actions (hide, reload data, etc.)
 */

import React, { useState } from "react";
import { MoreVertical, EyeOff, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Button } from "../../ui/button";
import { DashboardWidget, WidgetId } from "../types";

/**
 * Maps widget IDs to their React Query query keys
 */
function getWidgetQueryKey(widgetId: WidgetId): string[] {
  const queryKeyMap: Record<WidgetId, string[]> = {
    "retirement-readiness": ["retirement-readiness"],
    "aum-by-product": ["aum-by-product"],
    "contribution-rate": ["contribution-rate"],
    "social-security": ["social-security"],
    "retirement-income-sources": ["retirement-income-sources"],
    "fegli-risk": ["fegli-risk"],
    "age-distribution": ["client-age-distribution"],
    "rollover-opportunity": ["rollover-opportunity"],
    "employer-match": ["employer-match"],
    "location-insights": [], // No API endpoint yet
    "plan-type-spread": [], // No API endpoint yet
    "longevity-stress": [], // No API endpoint yet
  };
  
  return queryKeyMap[widgetId] || [];
}

interface WidgetOptionsMenuProps {
  widget: DashboardWidget;
  onHide: (instanceId: string) => void;
  disabled?: boolean;
}

export function WidgetOptionsMenu({
  widget,
  onHide,
  disabled = false,
}: WidgetOptionsMenuProps) {
  const queryClient = useQueryClient();
  const queryKey = getWidgetQueryKey(widget.widgetId);
  const hasQueryKey = queryKey.length > 0;
  const [isReloading, setIsReloading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleReload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!hasQueryKey || isReloading) return;
    
    setIsReloading(true);
    try {
      // Reset the query cache to clear it completely
      queryClient.resetQueries({ 
        queryKey,
        exact: true,
      });
      
      // Invalidate to mark as stale
      queryClient.invalidateQueries({ 
        queryKey,
        exact: true,
      });
      
      // Force refetch - remove type restriction to refetch all matching queries
      await queryClient.refetchQueries({ 
        queryKey,
        exact: true,
      });
      
      // Small delay to ensure UI feedback is visible
      await new Promise(resolve => setTimeout(resolve, 200));
    } catch (error) {
      console.error("Error reloading widget data:", error);
    } finally {
      setIsReloading(false);
      setIsOpen(false); // Close the dropdown menu
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 transition-all hover:bg-white hover:text-accent hover:scale-105 active:scale-95"
          disabled={disabled}
        >
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Widget options</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {hasQueryKey && (
          <DropdownMenuItem
            onClick={handleReload}
            className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
            disabled={isReloading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isReloading ? 'animate-spin' : ''}`} />
            {isReloading ? "Reloading..." : "Reload Data"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => {
            onHide(widget.instanceId);
            setIsOpen(false);
          }}
          className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
        >
          <EyeOff className="mr-2 h-4 w-4" />
          Hide
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
